import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AssetDetailsResponse } from "@switcherstudio/api-core-client";
import { Select } from "components/inputs/select/Select";
import styles from "./index.module.scss";

export interface AssetsSearchSortBarProps {
    assets: AssetDetailsResponse[];
    handleSort: (assets: AssetDetailsResponse[]) => void;
    showOrientation?: boolean;
}

enum SortOptions {
    Alphabetical = "Name A-Z",
    ReverseAlphabetical = "Name Z-A"
}

enum OrientationOptions {
    All = "All",
    Horizontal = "Horizontal",
    Vertical = "Vertical"
}

export const AssetsSearchSortBar = ({
    assets,
    handleSort,
    showOrientation = false
}: AssetsSearchSortBarProps) => {
    const { t } = useTranslation();

    const [selectedSortOption, setSelectedSortOption] =
        useState<keyof typeof SortOptions>("Alphabetical");
    const [selectedOrientation, setSelectedOrientation] =
        useState<OrientationOptions>(OrientationOptions.All);
    const [selectedTagOption, setSelectedTagOption] = useState<string[]>([]);

    // "tags" in code are production groups
    const allTags = assets.reduce<string[]>((acc, asset) => {
        const assetTags = asset?.Tags || [];
        return [...acc, ...assetTags];
    }, []);

    useEffect(() => {
        const filteredAndSortedAssets = [...assets]
            .sort((a, b) => {
                // Sort alphabetically
                if (selectedSortOption === "Alphabetical") {
                    return a.Name.localeCompare(b.Name);
                } else if (selectedSortOption === "ReverseAlphabetical") {
                    return b.Name.localeCompare(a.Name);
                }

                return 0;
            })
            .filter((asset) => {
                // Filter by orientation
                if (selectedOrientation !== OrientationOptions.All) {
                    const aspectRatio = asset.AspectRatios?.[0]?.Details;

                    if (aspectRatio?.Height && aspectRatio?.Width) {
                        const horizontal =
                            aspectRatio.Height <= aspectRatio.Width;

                        if (
                            selectedOrientation ===
                                OrientationOptions.Horizontal &&
                            !horizontal
                        )
                            return false;

                        if (
                            selectedOrientation ===
                                OrientationOptions.Vertical &&
                            horizontal
                        )
                            return false;
                    }
                }

                // Filter by tags
                if (
                    selectedTagOption.length > 0 &&
                    !selectedTagOption.some((tag) =>
                        asset.Tags.includes(tag)
                    ) &&
                    !selectedTagOption.includes("")
                ) {
                    return false;
                }

                return true;
            });

        handleSort(filteredAndSortedAssets);

        // Do not include handleSort in dependencies to prevent infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assets, selectedSortOption, selectedTagOption, selectedOrientation]);

    return (
        <fieldset className={styles["assets-search-sort-bar"]}>
            <Select
                id="assets-sort"
                onChange={(e) => {
                    setSelectedSortOption(
                        e.target.value as keyof typeof SortOptions
                    );
                }}
                options={Object.keys(SortOptions).map((option) => {
                    return {
                        value: option,
                        text: SortOptions[option]
                    };
                })}
                label={t("switcher-cloud:sort")}
                selected={selectedSortOption}
            />
            {showOrientation && (
                <Select
                    id="assets-orientation"
                    onChange={(e) =>
                        setSelectedOrientation(
                            e.target.value as OrientationOptions
                        )
                    }
                    options={Object.keys(OrientationOptions).map((option) => {
                        return {
                            value: option,
                            text: OrientationOptions[option]
                        };
                    })}
                    label={t("switcher-cloud:orientation")}
                    selected={selectedOrientation}
                />
            )}
            {allTags.length > 0 && (
                <Select
                    id="assets-production-group"
                    onChange={(e) => setSelectedTagOption([e.target.value])}
                    options={[
                        { value: "", text: t("cloud-assets:all") },
                        ...Array.from(new Set(allTags)).map((tag) => {
                            return {
                                value: tag,
                                text: tag
                            };
                        })
                    ]}
                    label={t("cloud:production-groups")}
                    selected={selectedTagOption[0]}
                />
            )}
        </fieldset>
    );
};
